import { BaseCanvasPack } from "./BaseCanvasPack";

export default class PyrotechnicCanvasPack extends BaseCanvasPack {
  drawBackground(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number,
    level?: number
  ): void {
    // Solid black for maximum contrast
    context.fillStyle = "#000000";
    context.fillRect(0, 0, width, height);

    if (frameTime !== undefined) {
      this.drawFireworks(context, width, height, frameTime);
      this.drawSmoke(context, width, height, frameTime);
    }
  }

  drawFrame(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    const borderWidth = 3;
    
    // Sparking Fuse Line
    if (frameTime !== undefined) {
        // Fast changing neon colors
        const hue = (frameTime * 0.2) % 360;
        context.strokeStyle = `hsl(${hue}, 100%, 50%)`;
        context.lineWidth = borderWidth;
        context.shadowBlur = 10;
        context.shadowColor = `hsl(${hue}, 100%, 50%)`;
        
        context.strokeRect(borderWidth/2, borderWidth/2, width - borderWidth, height - borderWidth);
        
        // Sparks traveling along the border
        this.drawSparks(context, width, height, frameTime, borderWidth);
        
        context.shadowBlur = 0;
    } else {
        context.strokeStyle = "#ff0000";
        context.lineWidth = borderWidth;
        context.strokeRect(borderWidth/2, borderWidth/2, width - borderWidth, height - borderWidth);
    }
  }

  drawOverlay(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    // None
  }

  private drawFireworks(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number
  ): void {
    context.save();
    context.globalCompositeOperation = "screen"; // Additive blending for light

    // Simulate distant fireworks explosions
    const explosionCount = 4;
    for (let i = 0; i < explosionCount; i++) {
        const seed = i * 789;
        // Explosion cycle: 3 seconds
        const cycle = 3000;
        // Stagger start times
        const t = (frameTime + seed * 500) % cycle;
        const progress = t / cycle;
        
        if (progress > 0.6) continue; // Only show for first part of cycle
        
        // Explosion parameters
        // Randomize position slightly based on cycle count to prevent static repetition
        const cycleCount = Math.floor((frameTime + seed * 500) / cycle);
        const posSeed = seed + cycleCount * 100;
        
        const x = (Math.sin(posSeed) * 0.4 + 0.5) * width;
        const y = (Math.cos(posSeed * 1.1) * 0.3 + 0.3) * height;
        
        // const maxRadius = 120;
        const particleCount = 30;
        const hue = (posSeed * 50) % 360;
        
        // Physics simulation
        // Initial explosion velocity
        const velocity = 150; // pixels per second approx
        const gravity = 200; // pixels per second squared
        
        // Time in seconds for physics
        const timeSec = progress * (cycle / 1000);
        
        context.fillStyle = `hsla(${hue}, 100%, 60%, ${1 - progress * 1.5})`;
        
        for (let j = 0; j < particleCount; j++) {
            const angle = (Math.PI * 2 * j) / particleCount;
            // Add some randomness to velocity for more natural look
            const pVelocity = velocity * (0.8 + Math.sin(j * 10 + posSeed) * 0.2);
            
            // Projectile motion: x = v*t, y = v*t + 0.5*g*t^2
            const dx = Math.cos(angle) * pVelocity * timeSec;
            const dy = Math.sin(angle) * pVelocity * timeSec + 0.5 * gravity * timeSec * timeSec;
            
            const px = x + dx;
            const py = y + dy;
            
            // Draw particle with trail
            context.beginPath();
            context.arc(px, py, 2, 0, Math.PI * 2);
            context.fill();
            
            // Trail
            context.beginPath();
            context.moveTo(px, py);
            context.lineTo(px - dx * 0.1, py - dy * 0.1); // Short trail opposite to motion
            context.strokeStyle = `hsla(${hue}, 100%, 60%, ${0.5 * (1 - progress)})`;
            context.lineWidth = 1;
            context.stroke();
        }
    }
    context.restore();
  }

  private drawSmoke(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number
  ): void {
    context.save();
    // Use "lighter" or "screen" for additive blending, but keep opacity low
    context.globalCompositeOperation = "screen"; 
    
    // Volumetric smoke rising - improved
    // Instead of circles, use a noise-like texture or multiple overlapping soft shapes
    const smokeCount = 6;
    for (let i = 0; i < smokeCount; i++) {
        const seed = i * 1234;
        const speed = 0.03;
        // Loop smoke rising
        const t = (frameTime * speed + seed * 1000);
        const progress = (t % height) / height; // 0 to 1 (bottom to top)
        
        // Wavy path
        const x = width/2 + Math.sin(t * 0.002) * (width * 0.4) + Math.cos(t * 0.005) * 50;
        const y = height - (progress * height);
        
        // Smoke puff grows and fades
        const size = 80 + progress * 150;
        const alpha = Math.sin(progress * Math.PI) * 0.1; // Fade in then out, low max opacity
        
        // Color shifts - dark grey/blue smoke with colored light reflections
        const hue = (frameTime * 0.01 + seed * 50) % 360;
        
        // Draw soft puff (radial gradient)
        const gradient = context.createRadialGradient(x, y, 0, x, y, size);
        gradient.addColorStop(0, `hsla(${hue}, 60%, 30%, ${alpha})`); // Darker core
        gradient.addColorStop(0.5, `hsla(${hue}, 60%, 20%, ${alpha * 0.5})`);
        gradient.addColorStop(1, "hsla(0, 0%, 0%, 0)");
        
        context.fillStyle = gradient;
        
        // Draw a more complex shape than a circle
        context.beginPath();
        // Distorted circle
        for (let a = 0; a <= Math.PI * 2; a += 0.5) {
            const r = size * (0.8 + Math.sin(a * 3 + t * 0.01) * 0.2);
            const px = x + Math.cos(a) * r;
            const py = y + Math.sin(a) * r;
            if (a === 0) context.moveTo(px, py);
            else context.lineTo(px, py);
        }
        context.closePath();
        context.fill();
    }
    context.restore();
  }

  private drawSparks(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number,
    borderWidth: number
  ): void {
    context.save();
    context.fillStyle = "#ffffff";
    context.shadowBlur = 5;
    context.shadowColor = "#ffffff";
    
    // Spark position travels around the perimeter
    const perimeter = 2 * (width + height);
    const speed = 0.8; // Faster
    const pos = (frameTime * speed) % perimeter;
    
    let x = 0, y = 0;
    
    if (pos < width) { // Top edge
        x = pos; y = borderWidth/2;
    } else if (pos < width + height) { // Right edge
        x = width - borderWidth/2; y = pos - width;
    } else if (pos < 2 * width + height) { // Bottom edge
        x = width - (pos - (width + height)); y = height - borderWidth/2;
    } else { // Left edge
        x = borderWidth/2; y = height - (pos - (2 * width + height));
    }
    
    // Draw spark burst
    const sparkCount = 12;
    for (let i = 0; i < sparkCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = Math.random() * 15;
        const size = Math.random() * 2;
        
        context.globalAlpha = Math.random(); // Flicker
        context.beginPath();
        context.arc(x + Math.cos(angle) * dist, y + Math.sin(angle) * dist, size, 0, Math.PI * 2);
        context.fill();
    }
    
    context.restore();
  }

  getBorderStyle(): string {
    return "3px solid #ff0000"; // Fallback
  }

  getBoxShadow(frameTime?: number): string {
    return "0 0 20px rgba(255, 0, 0, 0.5)";
  }

  getBackdropFilter(): string {
    return "none";
  }
}
